import * as React from 'react';
import { ListItemText, ListItemIcon, ListItemSecondaryAction, IconButton, Box } from '@mui/material';
import {
    Delete,
    FileCopy,
    Edit,
    PhotoSizeSelectLarge,
    FolderOutlined,
    ImageOutlined,
    DescriptionOutlined,
    CodeOutlined,
} from '@mui/icons-material';
import DangerButton from '../../_ho-forms-lib/src/MaterialComponents/DangerButton';
import { Debounce } from '../../utils/debounce';
import { isImagePath } from './FileManager';

import ListItemButton from "@mui/material/ListItemButton";

function pickFileIcon(path: string) {
    if (/[.](png|jpg|jpeg|svg|gif|webp|avif)$/i.test(path)) return ImageOutlined;
    if (/[.](js|jsx|ts|tsx|json|html|s?css|xml|yml|yaml|toml)$/i.test(path)) return CodeOutlined;
    return DescriptionOutlined;
}

interface FileManagerListItemProps {
    filePath: string;
    fileSize: number;
    targetFolder: string;
    pickOnly: boolean;
    onCopyPathClick: (e: any)=>void;
    onDeleteClick: (e: any)=>void;
    onRenameClick: (e: any)=>void;
    onActiveChange: (active: boolean, element: HTMLElement|null, filePath: string)=>void;
    onResizeClick: (e: any)=> void;
}

export interface FileManagerListItemState{
    hover: boolean;
}

export class FileManagerListItem extends React.Component<FileManagerListItemProps & {[key: string]: any}, any> {
    state = {
        hover: false
    };

    itemRef: any = React.createRef();

    hoverDebounce = new Debounce(100);

    static levelMatcher = /\//g;

    humanReadableSize(size: any){
        if(size>1000000){
            return Math.round(size/1000000).toString()+'mb';
        }
        else{
            return Math.round(size/1000).toString()+'kb';
        }

    }

    handleItemDelete = (e: any, ready: boolean) => {
        if(ready){
            this.props.onDeleteClick(e);
            if(this.state.hover){ this.props.onActiveChange(false, null, ''); }
        }
    }

    handleItemMouseOver = (e: React.SyntheticEvent)=>{
        this.hoverDebounce.run(()=>{
            if(this.state.hover===false){
                this.setState({hover:true});
                this.props.onActiveChange(true, this.itemRef.current, this.props.filePath);
            }
        });
    }

    handleItemMouseOut = (e: React.SyntheticEvent)=>{
        this.hoverDebounce.run(()=>{
            if(this.state.hover===true){
                this.setState({hover:false});
                this.props.onActiveChange(false, this.itemRef.current, this.props.filePath);
            }
        });
    }

    render(){
        const {onDeleteClick, onCopyPathClick, onActiveChange, pickOnly, button, filePath, fileSize, targetFolder, onRenameClick, onResizeClick, ...rest} = this.props;
        const isImage = isImagePath(filePath);
        const displayValue = filePath.replace(targetFolder,'/');
            if(displayValue === '/' ) return (null);
            const level = (displayValue.match(FileManagerListItem.levelMatcher)||[]).length;
            const isFolder = displayValue.endsWith('/');
            if(isFolder){
                if(level!==2) return (null);
            }
            else if(level!==1) return (null);

        const FileIcon = isFolder ? FolderOutlined : pickFileIcon(filePath);
        const displayLabel = isFolder ? displayValue.substr(1).replace(/\/$/, '') : displayValue.substr(1);
        const iconButtonSx = {
            color: 'rgba(11, 18, 48, 0.55)',
            padding: '6px',
            '&:hover': { color: '#0b1230', backgroundColor: 'rgba(11, 18, 48, 0.06)' },
            '&.Mui-disabled': { color: 'rgba(11, 18, 48, 0.22)' },
        } as const;

        return (
            <ListItemButton
                ref={this.itemRef}
                onMouseOver={this.handleItemMouseOver}
                onMouseOut={this.handleItemMouseOut}
                data-value={filePath}
                {...rest}>
                <ListItemIcon sx={{
                    minWidth: 36,
                    color: isFolder ? '#FA871C' : 'rgba(11, 18, 48, 0.5)',
                }}>
                    <FileIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                    primary={displayLabel}
                    secondary={isFolder ? 'Folder' : null}
                    slotProps={{
                        secondary: {
                            sx: {
                                fontFamily: '"JetBrains Mono", "SFMono-Regular", Menlo, monospace',
                                fontSize: 10.5,
                                fontWeight: 500,
                                letterSpacing: '0.14em',
                                textTransform: 'uppercase',
                                color: 'rgba(11, 18, 48, 0.42)',
                            },
                        },
                    }}
                />
                <ListItemSecondaryAction
                    onMouseOver={this.handleItemMouseOver}
                    onMouseOut={this.handleItemMouseOut}
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                >
                    {!isFolder && (
                        <Box component="span" sx={{
                            fontFamily: '"JetBrains Mono", monospace',
                            fontSize: 11,
                            fontWeight: 500,
                            letterSpacing: '-0.002em',
                            color: 'rgba(11, 18, 48, 0.5)',
                            mr: this.state.hover ? 0.5 : 0,
                            transition: 'margin 160ms ease',
                        }}>
                            {this.humanReadableSize(fileSize)}
                        </Box>
                    )}
                    <Box
                        // Without stopPropagation, clicking any of the action
                        // icons bubbles up to the ListItemButton and triggers
                        // its onClick (open file / enter folder) before the
                        // intended action runs.
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.25,
                            opacity: this.state.hover ? 1 : 0,
                            pointerEvents: this.state.hover ? 'auto' : 'none',
                            transition: 'opacity 160ms ease',
                        }}>
                        {!isFolder && (<IconButton
                            data-value={filePath}
                            onClick={onCopyPathClick}
                            size="small"
                            title="Copy path"
                            aria-label="Copy path"
                            sx={iconButtonSx}><FileCopy fontSize="small" /></IconButton>)}
                        <IconButton
                            disabled={pickOnly}
                            data-value={filePath}
                            onClick={onRenameClick}
                            size="small"
                            title="Rename"
                            aria-label="Rename"
                            sx={iconButtonSx}><Edit fontSize="small" /></IconButton>
                        {isImage && (<IconButton
                            disabled={pickOnly}
                            data-value={filePath}
                            onClick={onResizeClick}
                            size="small"
                            title="Resize image"
                            aria-label="Resize image"
                            sx={iconButtonSx}><PhotoSizeSelectLarge fontSize="small" /></IconButton>)}
                        <DangerButton
                            onClick={this.handleItemDelete}
                            loadedButton={<IconButton data-value={filePath} size="small" title="Confirm delete" aria-label="Confirm delete" sx={{ ...iconButtonSx, color: '#d83a52', '&:hover': { color: '#d83a52', backgroundColor: 'rgba(216, 58, 82, 0.10)' } }}><Delete fontSize="small" /></IconButton>}
                            button={<IconButton disabled={pickOnly} data-value={filePath} size="small" title="Delete" aria-label="Delete" sx={iconButtonSx}><Delete fontSize="small" /></IconButton>}
                        />
                    </Box>
                </ListItemSecondaryAction>
            </ListItemButton>
        );
    }
}